import React, { useEffect, useState } from "react";
import { Loader2, Check } from "lucide-react";
import { api, argent, jeton } from "./api";
import { Modale, Case, BoutonSombre } from "./ui";

const ETATS = {
  exact: { texte: "Solde couvert", fond: "#ECFDF5", bord: "#A7F3D0", couleur: "#047857" },
  partiel: { texte: "Paiement partiel", fond: "#FFFBEB", bord: "#FDE68A", couleur: "#92400E" },
  excedent: { texte: "Montant supérieur", fond: "#EFF6FF", bord: "#BFDBFE", couleur: "#1D4ED8" },
  deja_enregistre: { texte: "Déjà enregistré", fond: "#F3F4F6", bord: "#E5E7EB", couleur: "#6B7280" },
  /* La facture est déjà soldée en dehors du relevé (classeur de l'école, pointage à la
     main) : ce versement est très probablement le même argent. Il était annoncé « montant
     supérieur », ce qui laissait croire que la famille avait payé deux fois. */
  deja_paye: { texte: "Facture déjà payée", fond: "#F3F4F6", bord: "#E5E7EB", couleur: "#6B7280" },
  inconnu: { texte: "Référence inconnue", fond: "#F3F4F6", bord: "#E5E7EB", couleur: "#6B7280" },
  /* Mis de côté par la console : référence partagée par deux familles, ou deux versements
     identiques le même jour. Rien n'est enregistré tout seul : l'école tranche à la main. */
  a_verifier: { texte: "À vérifier", fond: "#FEF3C7", bord: "#FCD34D", couleur: "#92400E" },
  /* Virement sans le QR, rattaché à une famille par le nom (et le montant) : l'école
     confirme que c'est bien un paiement de cours avant qu'il soit enregistré. */
  a_confirmer: { texte: "Sans QR — à confirmer", fond: "#F5F3FF", bord: "#DDD6FE", couleur: "#6D28D9" },
  sans_reference: { texte: "Sans référence", fond: "#F3F4F6", bord: "#E5E7EB", couleur: "#6B7280" },
};

const RETENABLE = ["exact", "partiel", "excedent"];

const IGNORE = ["deja_enregistre", "deja_paye", "inconnu"];

const FILTRES = [
  { id: "tous", label: "Tous", test: () => true },
  { id: "ecarts", label: "Écarts", test: (v) => (v.etat === "partiel" || v.etat === "excedent") && !v.a_confirmer },
  { id: "exact", label: "Soldes couverts", test: (v) => v.etat === "exact" && !v.a_confirmer },
  /* Les virements sans le QR qui ressemblent à un paiement de cours (école, 26.10.2026). */
  { id: "sans_ref", label: "Sans référence — à confirmer", test: (v) => v.a_confirmer },
  { id: "verifier", label: "À vérifier", test: (v) => v.a_verifier },
  { id: "ignores", label: "Ignorés", test: (v) => IGNORE.includes(v.etat) && !v.a_verifier },
];

/* Import du relevé des crédits, rapprochement par référence de facture, puis validation. */
export default function ImportReleve({ releve, onFermer, onTermine, actionSecondaire }) {
  const [etape, setEtape] = useState("fichier");
  const [analyse, setAnalyse] = useState(null);
  const [choisis, setChoisis] = useState([]);
  const [resultat, setResultat] = useState(null);
  const [dernier, setDernier] = useState(releve || null);
  const [filtre, setFiltre] = useState("tous");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!releve) api("/releves/dernier").then(setDernier).catch(() => {});
  }, [releve]);

  const analyser = async (fichier) => {
    setEtape("analyse");
    setErr("");
    try {
      const corps = new FormData();
      corps.append("fichier", fichier);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/console/encaissements/analyser`, {
        method: "POST",
        headers: { Authorization: `Bearer ${jeton()}` },
        body: corps,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Erreur ${res.status}`);
      setAnalyse(data);
      setChoisis(data.versements.filter((v) => RETENABLE.includes(v.etat) && !v.a_confirmer)
        .map((v) => v.reference));
      if (data.resume.a_confirmer > 0) setFiltre("sans_ref");
      setEtape("apercu");
    } catch (e) {
      setErr(e.message);
      setEtape("fichier");
    }
  };

  const valider = async () => {
    setEtape("ecriture");
    setErr("");
    try {
      const r = await api("/encaissements/valider", {
        method: "POST",
        body: JSON.stringify({ lot: analyse.lot, refs: choisis }),
      });
      setResultat(r);
      setEtape("fini");
    } catch (e) {
      setErr(e.message);
      setEtape("apercu");
    }
  };

  const retenus = (analyse?.versements || []).filter((v) => RETENABLE.includes(v.etat));
  const total = retenus.filter((v) => choisis.includes(v.reference)).reduce((s, v) => s + v.montant, 0);
  const filtreActif = FILTRES.find((f) => f.id === filtre) || FILTRES[0];
  const visibles = (analyse?.versements || []).filter(filtreActif.test);

  return (
    <Modale
      titre="Relevé bancaire — encaissements"
      sous_titre="La console lit les références de facture du relevé et marque les paiements. Rien n'est écrit avant ta validation."
      onFermer={onFermer}
      testid="modale-releve"
      large
    >
      {etape === "fichier" && (
        <>
          {/* Deux comptes, deux relevés, deux dates : l'école doit voir d'un coup d'œil
              lequel des deux est en retard. Avant, une seule ligne — et importer celui de
              Noémie faisait croire que tout était à jour. */}
          <div className="rounded-xl p-4 mb-4 text-sm" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }}
            data-testid="releve-dernier">
            <strong>Les deux comptes de l'école</strong>
            <ul className="mt-2 space-y-2">
              {(dernier?.comptes || []).map((c) => (
                <li key={c.cle} data-testid={`releve-compte-${c.cle}`}>
                  <span style={{ fontWeight: 600 }}>
                    {c.valide ? "✓" : "⚠"} {c.libelle} — {c.banque}
                  </span>
                  <br />
                  {c.present
                    ? <>
                        {c.date_releve
                          ? `Paiements connus jusqu'au ${String(c.date_releve).split("-").reverse().join(".")}`
                          : "Aucune date lisible sur ce relevé"}
                        {` · ${c.nom} · importé le ${String(c.importe_le).split("-").reverse().join(".")}`}
                        {c.pourquoi ? <><br />{c.pourquoi}</> : null}
                      </>
                    : "Aucun relevé importé pour ce compte — dépose-le ci-dessous."}
                </li>
              ))}
            </ul>
            {dernier?.releve_plus_vieux_que_limport && (
              <div className="mt-2" style={{ fontWeight: 600 }} data-testid="releve-perime">
                ⚠ Le dernier relevé est bien plus ancien que le jour où il a été importé :
                c'est peut-être un vieil export. Recharge le relevé du jour avant de relancer.
              </div>
            )}
          </div>
          <p className="text-xs text-[var(--muted)] mb-4">
            La console reconnaît toute seule de quel compte vient le relevé (Raiffeisen pour
            l'école, PostFinance pour celui de Noémie) : dépose-les l'un après l'autre. Seuls
            les crédits porteurs d'une référence de facture (RF…) sont traités — factures de
            cours <strong>et de stages</strong>. Les autres versements sont ignorés, et un même
            versement n'est jamais enregistré deux fois.
          </p>
          <input type="file" accept="application/pdf" className="input-field"
            data-testid="releve-fichier"
            onChange={(e) => e.target.files[0] && analyser(e.target.files[0])} />
          {actionSecondaire && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
              <button className="btn-ghost" data-testid="releve-relancer-quand-meme"
                onClick={actionSecondaire.onClick}>
                {actionSecondaire.label}
              </button>
              <p className="text-xs text-[var(--muted)] mt-2">{actionSecondaire.aide}</p>
            </div>
          )}
        </>
      )}

      {(etape === "analyse" || etape === "ecriture") && (
        <p className="text-sm flex items-center gap-2" data-testid="releve-chargement">
          <Loader2 size={16} className="animate-spin" />
          {etape === "analyse" ? "Lecture du relevé et rapprochement…" : "Enregistrement des paiements…"}
        </p>
      )}

      {etape === "apercu" && analyse && (
        <>
          {/* De quel compte vient ce relevé : si la console ne le reconnaît pas, elle le dit
              plutôt que de faire passer l'autre compte pour à jour. */}
          <div className="rounded-xl px-4 py-2 mb-3 text-sm" data-testid="releve-compte-lu"
            style={analyse.compte
              ? { background: "#ECFDF5", border: "1px solid #A7F3D0", color: "#065F46" }
              : { background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B" }}>
            {analyse.compte
              ? <>Relevé reconnu : <strong>{analyse.compte_libelle}</strong>
                  {analyse.certitude === "iban" ? "" : (
                    /* Le « relevé de compte filtré » de Raiffeisen ne porte pas l'IBAN :
                       on dit alors à quoi on l'a reconnu, pour que l'école puisse vérifier. */
                    <span className="opacity-75">
                      {analyse.certitude === "banque"
                        ? ` — reconnu à la banque (${analyse.banque}), l'IBAN n'est pas imprimé sur ce relevé`
                        : " — reconnu à sa mise en page, l'IBAN n'est pas imprimé sur ce relevé"}
                    </span>
                  )}</>
              : <>Compte non reconnu sur ce relevé : ni l'IBAN, ni le nom de la banque n'y
                  figurent. Les paiements seront quand même pointés, mais ce relevé ne mettra
                  pas à jour la date d'un compte.</>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="releve-resume">
            <Chiffre titre="Versements lus" valeur={analyse.resume.versements} />
            <Chiffre titre="Rapprochés" valeur={analyse.resume.rapproches} />
            <Chiffre titre="À encaisser" valeur={argent(analyse.resume.montant)} />
            <Chiffre titre="Écarts" valeur={analyse.resume.ecarts ?? 0}
              onClick={() => setFiltre("ecarts")} />
            <Chiffre titre="Ignorés" valeur={analyse.resume.deja_enregistres
              + (analyse.resume.deja_payes || 0) + analyse.resume.inconnus} />
          </div>

          {(analyse.resume.deja_payes || 0) > 0 && (
            <div className="rounded-xl p-4 mb-4 text-sm" data-testid="releve-deja-payees"
              style={{ background: "#F1F5F9", border: "1px solid var(--border)" }}>
              <strong>{analyse.resume.deja_payes} versement(s) concernent une facture déjà
              soldée.</strong> Ces paiements étaient donc déjà connus de la console (pointés
              auparavant ou à la main) : ce relevé montre le même argent. Ils ne sont
              pas cochés et rien ne sera écrit — les encaisser doublerait le montant reçu.
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-3" data-testid="releve-filtres">
            {FILTRES.map((f) => {
              const nb = (analyse.versements || []).filter(f.test).length;
              return (
                <button key={f.id} onClick={() => setFiltre(f.id)} data-testid={`releve-filtre-${f.id}`}
                  className="px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-transform hover:-translate-y-0.5"
                  style={{
                    background: filtre === f.id ? "var(--primary)" : "#fff",
                    color: filtre === f.id ? "#04202A" : "var(--muted)",
                    borderColor: filtre === f.id ? "var(--primary)" : "var(--border)",
                  }}>
                  {f.label} ({nb})
                </button>
              );
            })}
          </div>

          {filtre === "sans_ref" && (() => {
            const aConfirmer = (analyse.versements || []).filter((v) => v.a_confirmer);
            const tousCoches = aConfirmer.length > 0 && aConfirmer.every((v) => choisis.includes(v.reference));
            return (
              <div className="rounded-xl p-4 mb-3 text-sm" data-testid="releve-sans-ref"
                style={{ background: "#F5F3FF", border: "1px solid #DDD6FE", color: "#4C1D95" }}>
                <strong>{aConfirmer.length} virement(s) sans le QR de la facture</strong> ressemblent
                à un paiement de cours : la console a trouvé une famille au même nom (et, le plus
                souvent, au même montant). Lis le payeur et le texte du virement, puis coche ceux
                qui sont bien des paiements de cours — rien n'est enregistré sans ta coche.
                {aConfirmer.length > 0 && (
                  <div className="mt-3 flex items-center gap-2">
                    <Case cochee={tousCoches} testid="releve-sans-ref-tous"
                      onChange={(c) => {
                        const refs = aConfirmer.map((v) => v.reference);
                        setChoisis(c ? [...new Set([...choisis, ...refs])] : choisis.filter((x) => !refs.includes(x)));
                      }} />
                    <span>Tout sélectionner ({aConfirmer.length})</span>
                  </div>
                )}
              </div>
            );
          })()}

          {filtre === "ecarts" && (
            <p className="text-xs mb-3 text-[var(--muted)]" data-testid="releve-aide-ecarts">
              Un écart n'est jamais une erreur de la console : le montant reçu ne correspond pas au
              solde dû. <strong>Montant supérieur</strong> = la famille a payé plus (paiement de
              deux factures en un seul virement, arrondi, ou inscription entre-temps).
              <strong> Paiement partiel</strong> = il reste un solde. Tu retrouves tous ces écarts
              après coup dans Facturation → <strong>Écarts de paiement</strong>.
            </p>
          )}

          {analyse.resume.reconnus === 0 && (
            <div className="rounded-xl p-4 mb-4 text-sm" data-testid="releve-aucun-match"
              style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B" }}>
              <strong>Aucune référence de facture 1000 ARTS reconnue dans ce fichier.</strong><br />
              Les {analyse.resume.versements} versement(s) lus portent des références qui ne
              correspondent à aucune de nos factures — c'est très probablement le mauvais document
              (relevé d'un autre compte, ou factures reçues d'un fournisseur). Il faut le relevé
              des <strong>crédits du compte de l'école</strong> exporté depuis l'e-banking, celui
              où chaque ligne indique « Crédit (SCOR) » et une référence commençant par
              <strong> RF…F2026S1…</strong>.
            </div>
          )}

          {analyse.resume.reconnus > 0 && analyse.resume.rapproches === 0 && (
            <div className="rounded-xl p-4 mb-4 text-sm" data-testid="releve-deja-traite"
              style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", color: "#065F46" }}>
              <strong>C'est bien le bon relevé : rien de nouveau à encaisser.</strong><br />
              Les {analyse.resume.reconnus} versement(s) reconnus ont déjà été enregistrés lors
              d'un import précédent — les factures concernées sont donc à jour. Tu peux fermer
              cette fenêtre : les relances sont ouvertes.
            </div>
          )}

          <div className="border rounded-xl overflow-auto max-h-80" style={{ borderColor: "var(--border)" }}>
            <table className="w-full text-sm">
              <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                {visibles.map((v) => {
                  const e = v.a_verifier ? ETATS.a_verifier : v.a_confirmer ? ETATS.a_confirmer
                    : v.sans_reference && v.etat === "inconnu" ? ETATS.sans_reference
                    : (ETATS[v.etat] || ETATS.inconnu);
                  const actif = RETENABLE.includes(v.etat);
                  return (
                    <tr key={v.reference + v.date + v.montant} data-testid="releve-ligne"
                      className={actif ? "" : "opacity-60"}>
                      <td className="px-3 py-2 w-8">
                        {actif && (
                          <Case cochee={choisis.includes(v.reference)} testid="releve-case"
                            onChange={(c) => setChoisis(c
                              ? [...choisis, v.reference]
                              : choisis.filter((x) => x !== v.reference))} />
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <div className="font-medium">{v.client || v.payeur || "—"}</div>
                        <div className="text-xs text-[var(--muted)]">
                          {v.numero || (v.sans_reference ? "sans référence" : v.reference)} · {v.date}
                          {v.paye_pour && ` · payé pour ${v.paye_pour}`}
                        </div>
                        {v.sans_reference && (
                          <div className="text-xs" style={{ color: "#6D28D9" }} data-testid="releve-sans-ref-detail">
                            Virement de « {v.payeur} »{v.communication ? ` · « ${v.communication} »` : ""}
                            {v.piste ? ` — ${v.piste}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <div className="font-semibold">{argent(v.montant)}</div>
                        {v.etat === "partiel" && (
                          <div className="text-xs" style={{ color: "#92400E" }}>
                            facture {argent(v.total_facture)}
                            {v.deja_recu > 0 ? ` · déjà reçu ${argent(v.deja_recu)}` : ""} ·
                            manque {argent(v.reste)}
                          </div>
                        )}
                        {v.etat === "excedent" && (
                          <div className="text-xs" style={{ color: "#1D4ED8" }}>
                            facture {argent(v.total_facture)}
                            {v.deja_recu > 0 ? ` · déjà reçu ${argent(v.deja_recu)}` : ""} ·
                            en trop {argent(v.ecart)}
                          </div>
                        )}
                        {v.a_verifier && v.motif && (
                          <div className="text-xs" style={{ color: "#92400E" }}>{v.motif}</div>
                        )}
                        {v.note && (
                          <div className="text-xs text-[var(--muted)]" data-testid="releve-note">{v.note}</div>
                        )}
                        {v.etat === "deja_paye" && (
                          <div className="text-xs text-[var(--muted)]">
                            facture {argent(v.total_facture)} · déjà encaissée
                            {v.deja_recu > 0 ? ` (${argent(v.deja_recu)} reçus)` : ""} — même argent
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                          style={{ background: e.fond, border: `1px solid ${e.bord}`, color: e.couleur }}>
                          {e.texte}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {analyse.resume.partiels > 0 && (
            <p className="text-xs mt-3" style={{ color: "#92400E" }}>
              {analyse.resume.partiels} paiement(s) partiel(s) : la facture passera en « Partiellement
              payée » avec son solde restant, et ne sera pas relancée automatiquement.
            </p>
          )}
          {err && <p className="text-sm mt-3" style={{ color: "#B91C1C" }}>{err}</p>}

          <div className="flex items-center gap-3 mt-5">
            {analyse.resume.rapproches > 0 ? (
              <BoutonSombre onClick={valider} disabled={choisis.length === 0} testid="releve-valider">
                <Check size={14} /> Enregistrer {choisis.length} paiement(s) · {argent(total)}
              </BoutonSombre>
            ) : (
              <BoutonSombre onClick={onTermine} testid="releve-continuer">
                <Check size={14} /> Relevé lu — continuer
              </BoutonSombre>
            )}
            <button className="btn-ghost" onClick={onFermer} data-testid="releve-annuler">Annuler</button>
            <span className="text-xs text-[var(--muted)] ml-auto">
              {retenus.length} versement(s) rapprochable(s) sur {analyse.resume.versements} ·
              {analyse.resume.stages > 0 ? ` dont ${analyse.resume.stages} de stage ·` : ""}
              total du relevé {argent(analyse.resume.montant_total)}
            </span>
          </div>
        </>
      )}

      {etape === "fini" && resultat && (
        <>
          <div className="rounded-xl p-4 text-sm" data-testid="releve-resultat"
            style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", color: "#047857" }}>
            {resultat.enregistres.length} paiement(s) enregistré(s) · {argent(resultat.montant)}.
            {resultat.partiels.length > 0 && ` ${resultat.partiels.length} facture(s) partiellement payée(s).`}
          </div>
          <p className="text-xs text-[var(--muted)] mt-3">
            Les factures soldées passent en « Payée » et ne remonteront plus dans les impayés.
            Les relances sont ouvertes.
          </p>
          <div className="flex gap-2 mt-5">
            <BoutonSombre onClick={onTermine} testid="releve-termine">Terminer</BoutonSombre>
          </div>
        </>
      )}
    </Modale>
  );
}

const Chiffre = ({ titre, valeur, onClick }) => (
  <div className="rounded-xl border p-3" style={{ borderColor: "var(--border)", cursor: onClick ? "pointer" : "default" }}
    onClick={onClick} data-testid={onClick ? "releve-chiffre-cliquable" : undefined}>
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{titre}</div>
    <div className="font-display text-lg mt-0.5">{valeur}</div>
  </div>
);
